import { Link, useRouter } from '@tanstack/react-router'
import { RotateCcw, SearchX, TriangleAlert } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { isApiError } from '@/lib/api'

/** 根 ErrorBoundary:任何路由抛出未捕获错误都落到这里,不给白屏。 */
export function RouteErrorBoundary({ error }: { error: Error }) {
  const router = useRouter()
  const status = isApiError(error) ? error.status : null

  return (
    <CenteredState
      icon={<TriangleAlert className="size-6" />}
      tone="danger"
      title={status === 0 ? '无法连接后端服务' : '页面出错了'}
      description={error.message || '发生了未预期的错误。'}
      hint={
        status === 0
          ? '请确认 Django 已在 127.0.0.1:8000 启动(见 gta_web_frontend/README.md 的两条命令)。'
          : undefined
      }
    >
      <Button
        variant="outline"
        onClick={() => {
          void router.invalidate()
        }}
      >
        <RotateCcw className="size-4" />
        重试
      </Button>
      <Button asChild>
        <Link to="/dashboard">回到概览</Link>
      </Button>
    </CenteredState>
  )
}

/** 404:路由表里没有的 URL。 */
export function RouteNotFound() {
  return (
    <CenteredState
      icon={<SearchX className="size-6" />}
      tone="neutral"
      title="页面不存在"
      description="你访问的地址没有对应的页面,可能是链接失效或路径拼写有误。"
    >
      <Button asChild>
        <Link to="/dashboard">回到概览</Link>
      </Button>
    </CenteredState>
  )
}

interface CenteredStateProps {
  icon: React.ReactNode
  tone: 'danger' | 'neutral'
  title: string
  description: string
  hint?: string | undefined
  children?: React.ReactNode
}

function CenteredState({ icon, tone, title, description, hint, children }: CenteredStateProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-console-bg px-6 py-12">
      <div className="console-card flex w-full max-w-lg flex-col items-center gap-3 px-8 py-10 text-center">
        <span
          className={
            tone === 'danger'
              ? 'inline-flex size-11 items-center justify-center rounded-md bg-status-danger/10 text-status-danger'
              : 'inline-flex size-11 items-center justify-center rounded-md bg-console-hover text-console-muted'
          }
        >
          {icon}
        </span>
        <p className="text-lg font-medium text-console-text">{title}</p>
        <p className="text-sm break-all text-console-muted">{description}</p>
        {hint && <p className="text-xs text-console-subtle">{hint}</p>}
        {children && <div className="mt-2 flex items-center gap-2">{children}</div>}
      </div>
    </div>
  )
}
